import React, { useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import Input from 'src/components/form/floating-label/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'src/components/common/modal/modal';
import { env } from 'src/constants/env';
import { fireEvent } from 'src/utils/analytics/google';
import 'src/utils/popup.scss';
import getValidationSchema from './validate';
import { toast } from 'react-toastify';
import css from './propose-news.module.scss';
import API from '../data/api';

ProposeNewsPopup.propTypes = {
  trigger: PropTypes.element
};

export default function ProposeNewsPopup({ trigger }) {
  const validationSchema = getValidationSchema();
  const captchaRef = useRef(null);
  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      url: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    formState: { errors, isSubmitSuccessful, defaultValues, isDirty, isValid }
  } = methods;
  const values = getValues();
  const isEdited = Object.keys(values).some((key) => values[key] !== defaultValues[key]);

  const resetCallback = useCallback(() => {
    return isSubmitSuccessful ? reset : () => {};
  }, [reset, isSubmitSuccessful]);

  useEffect(resetCallback, [resetCallback]);

  const onFormSubmit = async (data, close) => {
    try {
      const response = await API.proposeNews(data);
      if (response.status === 201) {
        toast.success(`Пропозиція для створення новини надіслана! Модератор опрацює Ваш запит`);
        reset();
        close();
        fireEvent('propose_news_form_submit', data);
      }
    } catch (error) {
      // NOSONAR
      toast.error('Виникла помилка. Спробуйте, будь ласка, пізніше.');
    }
  };

  const titleInputId = useId();
  const urlInputId = useId();
  const descriptionInputId = useId();

  return (
    <Modal trigger={trigger} onClose={reset} nested closeOnDocumentClick={!isEdited}>
      {(close) => (
        <div className={css.proposeNews}>
          <button className={css.close} onClick={close}>
            &times;
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}>
              <legend>
                <h1>Заповніть форму для створення новини</h1>
              </legend>
              <fieldset>
                <Input
                  label='Назва новини'
                  id={titleInputId}
                  name='title'
                  type='text'
                  error={errors?.title?.message}
                  placeholder='Моя новина'
                  required
                  {...registerAndClearOnChange('title')}
                />
                <Input
                  label='Посилання на сайт'
                  id={urlInputId}
                  name='url'
                  type='text'
                  error={errors?.url?.message}
                  required
                  placeholder='http//:example.com'
                  {...registerAndClearOnChange('url')}
                />
                <Input
                  label='Коментар'
                  id={descriptionInputId}
                  name='description'
                  multiline={true}
                  error={errors?.description?.message}
                  {...registerAndClearOnChange('description')}
                />
              </fieldset>
              <button disabled={!isDirty || !isValid} type='submit'>
                Надіслати
              </button>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
        </div>
      )}
    </Modal>
  );
}
